import type { OrderViewsInput, Role, SaveViewInput, ViewChatDto, ViewChatInput, ViewDto, ViewSummaryDto, ViewTile, ViewVersionDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText, Field } from '../../ui';
import { KpiBuilder } from './KpiBuilder';
import { Headline, KpiChart } from './KpiChart';

/**
 * Le viste personali (ADR 0194 § 2): pagine di riquadri che la persona compone con il costruttore dei grafici. Ogni
 * salvataggio è una versione; il titolare la condivide con dei ruoli, che la vedono con i loro soli dati e la duplicano.
 */
export function ViewsPage({ initialId }: { initialId?: string }) {
  const [id, setId] = useState<string | null>(initialId ?? null);
  return id ? <ViewPage id={id} onBack={() => setId(null)} onOpen={setId} /> : <ViewsList onOpen={setId} />;
}

function ViewsList({ onOpen }: { onOpen: (id: string) => void }) {
  const { t, isManager } = useTenant();
  const q = useTenantQuery<{ data: ViewSummaryDto[] }>(['views'], '/views');
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api<ViewDto>(t('/views'), { body: { nome: name.trim(), riquadri: [] } satisfies SaveViewInput }),
    onSuccess: (v) => onOpen(v.id),
  });
  const qc = useQueryClient();
  // L'ordine delle viste nel menu della persona: si sposta una vista e si salva l'ordine di tutte.
  const order = useMutation({
    mutationFn: (ids: string[]) => api(t('/views/order'), { method: 'PUT', body: { ordine: ids } satisfies OrderViewsInput }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenant'] }),
  });
  const all = q.data?.data ?? [];
  // Per il titolare le viste di partenza dei ruoli stanno a parte: sono sue, una per ruolo, composte dal server.
  const starts = isManager ? all.filter((v) => v.di_partenza && v.mia) : [];
  const views = all.filter((v) => !starts.includes(v));
  const move = (i: number, by: -1 | 1) => {
    const ids = views.map((v) => v.id);
    [ids[i], ids[i + by]] = [ids[i + by]!, ids[i]!];
    order.mutate(ids);
  };
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Viste</h1>
        <p className="text-sm text-ink/60">Pagine di grafici e numeri che ti componi sui dati che puoi vedere. Quelle condivise con il tuo ruolo le puoi copiare e cambiare.</p>
      </div>
      <Card className="!p-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim().length >= 2) create.mutate();
          }}
        >
          <div className="min-w-[14rem] flex-1">
            <Field label="Nuova vista" placeholder="Per esempio: incassi del mese" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button disabled={name.trim().length < 2 || create.isPending}>{create.isPending ? 'Creo…' : 'Crea'}</Button>
        </form>
        <ErrorText>{create.error ? errorMessage(create.error) : ''}</ErrorText>
      </Card>
      <ErrorText>{q.error ? errorMessage(q.error) : ''}</ErrorText>
      {q.isSuccess && !views.length && <p className="text-sm text-ink/60">Non hai ancora viste.</p>}
      <ErrorText>{order.error ? errorMessage(order.error) : ''}</ErrorText>
      <div className="grid gap-3 sm:grid-cols-2">
        {views.map((v, i) => (
          <div key={v.id} role="button" tabIndex={0} className="cursor-pointer rounded-xl bg-white p-4 text-left shadow-sm hover:ring-1 hover:ring-accent/40" onClick={() => onOpen(v.id)} onKeyDown={(e) => e.key === 'Enter' && onOpen(v.id)}>
            <div className="flex items-start gap-2">
              <p className="min-w-0 flex-1 font-medium">{v.nome}</p>
              {views.length > 1 && (
                <span className="flex gap-1 text-xs text-ink/45">
                  <button type="button" aria-label="Sposta prima" disabled={i === 0 || order.isPending} className="px-1 hover:text-ink disabled:opacity-30" onClick={(e) => { e.stopPropagation(); move(i, -1); }}>↑</button>
                  <button type="button" aria-label="Sposta dopo" disabled={i === views.length - 1 || order.isPending} className="px-1 hover:text-ink disabled:opacity-30" onClick={(e) => { e.stopPropagation(); move(i, 1); }}>↓</button>
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-ink/55">
              {v.riquadri} {v.riquadri === 1 ? 'riquadro' : 'riquadri'} · versione {v.versione} · {v.mia ? 'tua' : `di ${v.proprietario}`}
            </p>
            {v.ruoli.length > 0 && <Badge tone="blue">condivisa con {v.ruoli.length === 1 ? 'un ruolo' : `${v.ruoli.length} ruoli`}</Badge>}
            {v.di_partenza && <Badge tone="green">vista di partenza del ruolo</Badge>}
          </div>
        ))}
      </div>
      {starts.length > 0 && (
        <div>
          <h2 className="mb-1 text-sm font-semibold">Viste di partenza dei ruoli</h2>
          <p className="mb-2 text-xs text-ink/55">Una per ruolo, composta dalle procedure e dai permessi del ruolo. Le persone del ruolo la trovano nelle loro viste. Se la cambi resta come l'hai fatta tu.</p>
          <ul className="space-y-1 text-sm">
            {starts.map((v) => (
              <li key={v.id}>
                <button type="button" className="text-left hover:text-accent" onClick={() => onOpen(v.id)}>{v.nome}</button>
                <span className="text-xs text-ink/50"> · {v.riquadri} {v.riquadri === 1 ? 'riquadro' : 'riquadri'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ViewPage({ id, onBack, onOpen }: { id: string; onBack: () => void; onOpen: (id: string) => void }) {
  const { t, isManager } = useTenant();
  const qc = useQueryClient();
  const q = useTenantQuery<ViewDto>(['view', id], `/views/${id}`);
  const [panel, setPanel] = useState<'' | 'chat' | 'riquadro' | 'versioni' | 'condividi'>('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['tenant'] });
  const v = q.data;
  const tilesOf = (x: ViewDto): ViewTile[] => x.riquadri.map(({ id: tid, titolo, larghezza, domanda }) => ({ id: tid, titolo, larghezza, domanda }));
  const save = useMutation({
    mutationFn: (riquadri: ViewTile[]) => api<ViewDto>(t(`/views/${id}`), { method: 'PUT', body: { nome: v!.nome, riquadri } satisfies SaveViewInput }),
    onSuccess: () => {
      setPanel('');
      refresh();
    },
  });
  const duplicate = useMutation({ mutationFn: () => api<ViewDto>(t(`/views/${id}/duplicate`), { body: {} }), onSuccess: (c) => { refresh(); onOpen(c.id); } });
  const remove = useMutation({ mutationFn: () => api(t(`/views/${id}/remove`), { body: {} }), onSuccess: () => { refresh(); onBack(); } });
  const error = q.error ?? save.error ?? duplicate.error ?? remove.error;
  if (!v) return <div><Button variant="ghost" onClick={onBack}>← Viste</Button><ErrorText>{error ? errorMessage(error) : ''}</ErrorText></div>;
  const tileId = (titolo: string) => {
    const base = titolo.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 30) || 'riquadro';
    let k = base;
    for (let n = 2; v.riquadri.some((r) => r.id === k); n++) k = `${base}_${n}`;
    return k;
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[16rem] flex-1">
          <Button variant="ghost" className="mb-1 px-2 py-1 text-xs" onClick={onBack}>← Viste</Button>
          <h1 className="text-xl font-semibold">{v.nome}</h1>
          <p className="text-xs text-ink/55">Versione {v.versione} · {v.mia ? 'tua' : `di ${v.proprietario}: puoi farne una copia tua`} · i numeri sono calcolati adesso, con i dati che puoi vedere</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {v.mia && <Button onClick={() => setPanel(panel === 'chat' ? '' : 'chat')}>Chiedi un riquadro</Button>}
          {v.mia && <Button variant="ghost" onClick={() => setPanel(panel === 'riquadro' ? '' : 'riquadro')}>Componilo a mano</Button>}
          <Button variant="ghost" onClick={() => setPanel(panel === 'versioni' ? '' : 'versioni')}>Versioni</Button>
          {v.mia && isManager && <Button variant="ghost" onClick={() => setPanel(panel === 'condividi' ? '' : 'condividi')}>Condividi</Button>}
          {!v.mia && <Button variant="ghost" disabled={duplicate.isPending} onClick={() => duplicate.mutate()}>Fanne una copia</Button>}
          {v.mia && <Button variant="ghost" disabled={remove.isPending} onClick={() => window.confirm(`Togliere la vista «${v.nome}»? Resta nelle versioni ma non si apre più.`) && remove.mutate()}>Togli</Button>}
        </div>
      </div>
      <ErrorText>{error ? errorMessage(error) : ''}</ErrorText>
      {panel === 'chat' && <ViewChat id={id} pending={save.isPending} onAdd={(tile) => save.mutate([...tilesOf(v), tile])} onCancel={() => setPanel('')} />}
      {panel === 'riquadro' && <KpiBuilder onCancel={() => setPanel('')} forView={{ onTile: (titolo, domanda) => { const { name: _n, ...d } = domanda; save.mutate([...tilesOf(v), { id: tileId(titolo), titolo, larghezza: 1, domanda: d }]); } }} />}
      {panel === 'versioni' && <Versions id={id} mine={v.mia} current={v.versione} onRestored={refresh} />}
      {panel === 'condividi' && <Share v={v} onSaved={() => { setPanel(''); refresh(); }} />}
      {!v.riquadri.length && <p className="text-sm text-ink/60">{v.mia ? 'Aggiungi il primo riquadro: scegli il dato, cosa misurare e come mostrarlo.' : 'Questa vista non ha ancora riquadri.'}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {v.riquadri.map((r, i) => (
          <Card key={r.id} className={`!p-4 ${r.larghezza === 2 ? 'md:col-span-2' : ''}`}>
            <div className="mb-2 flex items-baseline gap-2">
              <h3 className="min-w-0 flex-1 text-sm font-semibold">{r.titolo}</h3>
              {v.mia && (
                <span className="flex gap-2 text-xs text-ink/50">
                  <button type="button" disabled={i === 0 || save.isPending} className="hover:text-ink disabled:opacity-30" onClick={() => { const x = tilesOf(v); [x[i - 1], x[i]] = [x[i]!, x[i - 1]!]; save.mutate(x); }}>↑</button>
                  <button type="button" disabled={save.isPending} className="hover:text-ink" onClick={() => save.mutate(tilesOf(v).map((x) => (x.id === r.id ? { ...x, larghezza: x.larghezza === 2 ? 1 : 2 } : x)))}>{r.larghezza === 2 ? 'stringi' : 'allarga'}</button>
                  <button type="button" disabled={save.isPending} className="hover:text-ink" onClick={() => save.mutate(tilesOf(v).filter((x) => x.id !== r.id))}>togli</button>
                </span>
              )}
            </div>
            {r.chiuso ? (
              <p className="rounded-md bg-black/[0.03] p-3 text-sm text-ink/60">Riquadro chiuso: {r.chiuso}.</p>
            ) : r.risultato?.errore ? (
              <ErrorText>{r.risultato.errore}</ErrorText>
            ) : r.risultato?.manca?.length ? (
              <p className="text-sm text-ink/60">Ancora da raccogliere: servono voci di {r.risultato.manca.map((m) => m.nome).join(' e ')}.</p>
            ) : r.risultato?.serie?.length ? (
              <>
                <Headline k={r.risultato} />
                <KpiChart k={r.risultato} />
              </>
            ) : (
              <p className="text-sm text-ink/60">Nessun valore.</p>
            )}
            {r.risultato?.copertura && !r.chiuso && (
              <p className="mt-2 text-[11px] text-ink/45">
                {r.risultato.copertura.righe_totali} righe{r.risultato.copertura.righe_escluse ? `, ${r.risultato.copertura.righe_escluse} senza il valore o la data` : ''}
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function Versions({ id, mine, current, onRestored }: { id: string; mine: boolean; current: number; onRestored: () => void }) {
  const { t } = useTenant();
  const q = useTenantQuery<{ data: ViewVersionDto[] }>(['view-versions', id, current], `/views/${id}/versions`);
  const restore = useMutation({ mutationFn: (n: number) => api(t(`/views/${id}/versions/${n}/restore`), { body: {} }), onSuccess: onRestored });
  return (
    <Card className="!p-4">
      <h3 className="mb-2 text-sm font-semibold">Versioni</h3>
      <ErrorText>{q.error || restore.error ? errorMessage(q.error ?? restore.error) : ''}</ErrorText>
      <ul className="space-y-1 text-sm">
        {(q.data?.data ?? []).map((x) => (
          <li key={x.versione} className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{x.versione}</span>
            <span className="min-w-0 flex-1 text-ink/70">
              {x.nome} · {x.riquadri} {x.riquadri === 1 ? 'riquadro' : 'riquadri'} · {x.salvata_da}, {new Date(x.salvata_il).toLocaleString('it-IT')}
            </span>
            {x.versione === current ? <Badge tone="green">attuale</Badge> : mine && <Button variant="ghost" className="px-2 py-1 text-xs" disabled={restore.isPending} onClick={() => restore.mutate(x.versione)}>Ripristina</Button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Share({ v, onSaved }: { v: ViewDto; onSaved: () => void }) {
  const { t } = useTenant();
  const roles = useTenantQuery<{ data: Role[] }>(['roles'], '/roles');
  const [chosen, setChosen] = useState<string[]>(v.ruoli);
  const save = useMutation({ mutationFn: () => api(t(`/views/${v.id}/share`), { method: 'PUT', body: { ruoli: chosen } }), onSuccess: onSaved });
  return (
    <Card className="!p-4">
      <h3 className="text-sm font-semibold">Condividi con i ruoli</h3>
      <p className="mb-2 text-xs text-ink/55">Le persone di questi ruoli la vedono con i soli dati che possono vedere; non la cambiano, ne fanno una copia.</p>
      <div className="flex flex-wrap gap-1.5">
        {(roles.data?.data ?? []).map((r) => (
          <label key={r.key} className={`cursor-pointer rounded-md border px-2 py-1 text-xs ${chosen.includes(r.key) ? 'border-accent bg-accent/10 text-accent' : 'border-black/10 text-ink/70'}`}>
            <input type="checkbox" className="sr-only" checked={chosen.includes(r.key)} onChange={(e) => setChosen((x) => (e.target.checked ? [...x, r.key] : x.filter((k) => k !== r.key)))} />
            {r.label}
          </label>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Salvo…' : 'Salva'}</Button>
        <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      </div>
    </Card>
  );
}

/**
 * La chat della vista (ADR 0194 § 2): la persona chiede un riquadro scrivendo; il server risponde con il riquadro già
 * controllato e calcolato sui suoi dati. Si aggiunge solo quando la persona lo conferma.
 */
function ViewChat({ id, pending, onAdd, onCancel }: { id: string; pending: boolean; onAdd: (tile: ViewTile) => void; onCancel: () => void }) {
  const { t } = useTenant();
  const [text, setText] = useState('');
  const [history, setHistory] = useState<ViewChatInput['storia']>([]);
  const [last, setLast] = useState<ViewChatDto | null>(null);
  const ask = useMutation({
    mutationFn: (messaggio: string) => api<ViewChatDto>(t(`/views/${id}/chat`), { body: { messaggio, storia: history.slice(-10) } satisfies ViewChatInput }),
    onSuccess: (r, messaggio) => {
      setHistory((h) => [...h, { ruolo: 'persona' as const, testo: messaggio }, { ruolo: 'timonio' as const, testo: r.risposta }].slice(-10));
      setLast(r);
      setText('');
    },
  });
  return (
    <Card className="!p-4">
      <h3 className="text-sm font-semibold">Chiedi un riquadro</h3>
      <p className="mb-2 text-xs text-ink/55">Scrivi cosa vuoi vedere, per esempio «le fatture non pagate per cliente negli ultimi sei mesi». Timonio usa solo i dati che puoi vedere.</p>
      {history.length > 0 && (
        <ul className="mb-2 space-y-1 text-sm">
          {history.map((m, i) => (
            <li key={i} className={m.ruolo === 'persona' ? 'text-ink' : 'text-ink/65'}>
              <span className="font-medium">{m.ruolo === 'persona' ? 'Tu' : 'Timonio'}:</span> {m.testo}
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim().length >= 2) ask.mutate(text.trim());
        }}
      >
        <div className="min-w-[14rem] flex-1">
          <Field label="Cosa vuoi vedere" value={text} maxLength={1000} onChange={(e) => setText(e.target.value)} />
        </div>
        <Button disabled={text.trim().length < 2 || ask.isPending}>{ask.isPending ? 'Compongo…' : 'Chiedi'}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Chiudi</Button>
      </form>
      <ErrorText>{ask.error ? errorMessage(ask.error) : ''}</ErrorText>
      {last?.riquadro && (
        <div className="mt-3 rounded-lg border border-black/10 p-3">
          <p className="mb-1 text-sm font-semibold">{last.riquadro.titolo}</p>
          {last.anteprima?.serie?.length ? (
            <>
              <Headline k={last.anteprima} />
              <KpiChart k={last.anteprima} />
            </>
          ) : (
            <p className="text-sm text-ink/60">{last.anteprima?.manca?.length ? `Ancora da raccogliere: servono voci di ${last.anteprima.manca.map((m) => m.nome).join(' e ')}.` : 'Nessun valore per ora.'}</p>
          )}
          <Button className="mt-2" disabled={pending} onClick={() => { onAdd(last.riquadro!); setLast(null); }}>{pending ? 'Aggiungo…' : 'Aggiungi alla vista'}</Button>
        </div>
      )}
    </Card>
  );
}
